import { Component, OnInit, ViewChild } from '@angular/core';

import { FormsModule, NgModel } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { MatDividerModule } from '@angular/material/divider';

import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';

import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { ConfiguracaoNFCeService } from '../../../../shared/services/apis/dfe/configuracao-nfce.service';
import { ConfiguracaoNFCeRequest } from '../../../../shared/services/requests/dfe/configuracao-nfce.request';
import {
  SyncExcecoesRejeicaoNFCeRequest,
  SyncExcecaoRejeicaoNFCeItem,
} from '../../../../shared/services/requests/dfe/sync-excecoes-rejeicao-nfce.request';
import { ConfiguracaoNFCeResponse } from '../../../../shared/services/responses/dfe/configuracao-nfce.response';
import { ListaExcecaoRejeicaoNFCeResponse } from '../../../../shared/services/responses/dfe/lista-excecao-rejeicao-nfce.response';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { EmpresaCscService } from '../../../../shared/services/apis/dfe/empresa_csc.service';
import { EmpresaCscResponse } from '../../../../shared/services/responses/dfe/empresa_csc.response';
import { EmpresaCscRequest } from '../../../../shared/services/requests/dfe/empresa_csc.request';
import { firstValueFrom } from 'rxjs';

interface ExcecaoRow {
  idExcecaoRejeicaoNFCe: number | null;
  codigoRejeicao: number | null;
  descricaoRejeicao: string;
}

interface CscValidationResult {
  isValid: boolean;
  errorField: 'idCsc' | 'tokenCsc' | null;
  message: string | null;
}

@Component({
  selector: 'app-config-nfce',
  standalone: true,
  imports: [
    FormsModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatTableModule,
    MatDividerModule,
    HeaderComponent,
    LoadingBoxComponent,
    FooterActionComponent,
    EmptyStateComponent,
  ],
  templateUrl: './config-nfce.component.html',
  styleUrls: ['./config-nfce.component.css'],
})
export class ConfigNFCeComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_NotasFiscais;

  public isLoading = true;
  public isLoadingSalvar = false;

  public emitirNFCeRejeitadaEmContingencia = true;
  public idConfiguracaoNFCe: number | null = null;

  public idCsc: string = '';
  public tokenCsc: string = '';
  public idCscError: string | null = null;
  public tokenCscError: string | null = null;

  public excecoes: ExcecaoRow[] = [];
  public displayedColumns: string[] = ['codigoRejeicao', 'descricaoRejeicao', 'acoes'];

  @ViewChild('idCscModel') private idCscModel?: NgModel;
  @ViewChild('tokenCscModel') private tokenCscModel?: NgModel;

  constructor(
    private router: Router,
    private configuracaoNFCeService: ConfiguracaoNFCeService,
    private alertService: AlertService,
    private empresaCscService: EmpresaCscService
  ) {}

  ngOnInit(): void {
    this.carregarConfiguracao();
    this.carregarCsc();
  }

  private carregarCsc(): void {
    this.empresaCscService.getEmpresaCsc().subscribe({
      next: (response: EmpresaCscResponse) => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }
        this.idCsc = response.idCsc ?? '';
        this.tokenCsc = response.tokenCsc ?? '';
        this.atualizarValidacaoCsc();
      },
      error: () => {
        this.alertService.show('Erro', 'Falha ao carregar CSC.', AlertType.error);
      },
    });
  }

  private carregarConfiguracao(): void {
    this.isLoading = true;
    this.configuracaoNFCeService.get().subscribe({
      next: (response: ConfiguracaoNFCeResponse) => {
        this.isLoading = false;

        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }

        if (response.idConfiguracaoNFCe) {
          this.idConfiguracaoNFCe = response.idConfiguracaoNFCe;
          this.emitirNFCeRejeitadaEmContingencia = response.emitirNFCeRejeitadaEmContingencia;
        } else {
          this.emitirNFCeRejeitadaEmContingencia = true;
        }

        this.excecoes = (response.excecoesRejeicao || []).map(e => ({
          idExcecaoRejeicaoNFCe: e.idExcecaoRejeicaoNFCe,
          codigoRejeicao: e.codigoRejeicao,
          descricaoRejeicao: e.descricaoRejeicao,
        }));
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Falha ao carregar configurações de NFC-e.', AlertType.error);
      },
    });
  }

  adicionarExcecao(): void {
    this.excecoes = [
      ...this.excecoes,
      {
        idExcecaoRejeicaoNFCe: null,
        codigoRejeicao: null,
        descricaoRejeicao: '',
      },
    ];
  }

  removerExcecao(index: number): void {
    this.excecoes = this.excecoes.filter((_, i) => i !== index);
  }

  limitarCodigoRejeicao(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
    if (input.value.length > 4) {
      input.value = input.value.slice(0, 4);
    }
    this.excecoes[index].codigoRejeicao = input.value ? Number(input.value) : null;
  }

  bloquearNaoNumerico(event: KeyboardEvent): void {
    const permitidos = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (permitidos.includes(event.key)) return;
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onToggleChange(checked: boolean): void {
    this.emitirNFCeRejeitadaEmContingencia = checked;
  }

  onIdCscChange(value: string): void {
    this.idCsc = this.normalizarIdCsc(value);
    this.idCscModel?.control.markAsTouched();
    this.atualizarValidacaoCsc();
  }

  onTokenCscChange(): void {
    this.tokenCscModel?.control.markAsTouched();
    this.atualizarValidacaoCsc();
  }

  async salvar(): Promise<void> {
    if (this.isLoadingSalvar) return;

    const idCsc = (this.idCsc ?? '').trim();
    const tokenCsc = (this.tokenCsc ?? '').trim();

    this.idCsc = idCsc;
    this.tokenCsc = tokenCsc;
    this.marcarCamposCscComoTocados();

    const validacaoCsc = this.atualizarValidacaoCsc();
    if (!validacaoCsc.isValid) {
      this.alertService.show('Atenção', validacaoCsc.message ?? 'Dados do CSC inválidos.', AlertType.warning);
      return;
    }

    if (this.emitirNFCeRejeitadaEmContingencia) {
      for (const exc of this.excecoes) {
        if (!exc.codigoRejeicao || !exc.descricaoRejeicao?.trim()) {
          this.alertService.show(
            'Atenção',
            'Existem exceções com dados incompletos. Preencha o código e a descrição de todas as linhas.',
            AlertType.warning
          );
          return;
        }
      }
    }

    this.isLoadingSalvar = true;

    try {
      await this.salvarCsc(idCsc, tokenCsc);
    } catch {
      this.isLoadingSalvar = false;
      return;
    }

    const configRequest = new ConfiguracaoNFCeRequest();
    configRequest.emitirNFCeRejeitadaEmContingencia = this.emitirNFCeRejeitadaEmContingencia;

    this.configuracaoNFCeService.save(configRequest).subscribe({
      next: (response: ConfiguracaoNFCeResponse) => {
        if (response.isError) {
          this.isLoadingSalvar = false;
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }

        this.idConfiguracaoNFCe = response.idConfiguracaoNFCe;
        this.processarExcecoes();
      },
      error: () => {
        this.isLoadingSalvar = false;
        this.alertService.show('Erro', 'Falha ao salvar configurações.', AlertType.error);
      },
    });
  }

  private async salvarCsc(idCsc: string, tokenCsc: string): Promise<void> {
    const request = new EmpresaCscRequest();
    request.idCsc = idCsc;
    request.tokenCsc = tokenCsc;

    const response = await firstValueFrom(this.empresaCscService.postEmpresaCsc(request));
    if (response.isError) {
      this.alertService.show('Erro', response.errorDescription, AlertType.error);
      throw new Error(response.errorDescription);
    }
  }

  private atualizarValidacaoCsc(): CscValidationResult {
    const validacao = this.validarCscToken(this.idCsc, this.tokenCsc);

    this.idCscError = validacao.errorField === 'idCsc' ? validacao.message : null;
    this.tokenCscError = validacao.errorField === 'tokenCsc' ? validacao.message : null;

    return validacao;
  }

  public deveExibirIdCscErro(): boolean {
    return !!this.idCscError && !!this.idCscModel?.touched;
  }

  public deveExibirTokenCscErro(): boolean {
    return !!this.tokenCscError && !!this.tokenCscModel?.touched;
  }

  private validarCscToken(idToken: string, csc: string): CscValidationResult {
    const idTokenNormalizado = (idToken ?? '').trim();
    const cscNormalizado = (csc ?? '').trim();

    if (!idTokenNormalizado) {
      return {
        isValid: false,
        errorField: 'idCsc',
        message: 'IdToken do CSC não informado.',
      };
    }

    const id = Number(idTokenNormalizado);
    if (!/^\d{6}$/.test(idTokenNormalizado) || !Number.isSafeInteger(id) || id <= 0 || id > 2147483647) {
      return {
        isValid: false,
        errorField: 'idCsc',
        message: 'IdToken do CSC deve conter 6 dígitos numéricos.',
      };
    }

    if (!cscNormalizado) {
      return {
        isValid: false,
        errorField: 'tokenCsc',
        message: 'Código CSC não informado.',
      };
    }

    if (cscNormalizado.length < 6) {
      return {
        isValid: false,
        errorField: 'tokenCsc',
        message: 'Código CSC inválido.',
      };
    }

    if (cscNormalizado.includes(' ')) {
      return {
        isValid: false,
        errorField: 'tokenCsc',
        message: 'Código CSC não pode conter espaços.',
      };
    }

    return {
      isValid: true,
      errorField: null,
      message: null,
    };
  }

  private normalizarIdCsc(value: string): string {
    return (value ?? '').replace(/\D/g, '').slice(0, 6);
  }

  private marcarCamposCscComoTocados(): void {
    this.idCscModel?.control.markAsTouched();
    this.tokenCscModel?.control.markAsTouched();
    this.idCscModel?.control.updateValueAndValidity({ emitEvent: false });
    this.tokenCscModel?.control.updateValueAndValidity({ emitEvent: false });
  }

  private processarExcecoes(): void {
    const syncRequest = new SyncExcecoesRejeicaoNFCeRequest();
    syncRequest.excecoes = this.excecoes.map(exc => {
      const item = new SyncExcecaoRejeicaoNFCeItem();
      item.idExcecaoRejeicaoNFCe = exc.idExcecaoRejeicaoNFCe;
      item.codigoRejeicao = exc.codigoRejeicao!;
      item.descricaoRejeicao = exc.descricaoRejeicao;
      return item;
    });

    this.configuracaoNFCeService.syncExcecoes(syncRequest).subscribe({
      next: (response: ListaExcecaoRejeicaoNFCeResponse) => {
        this.isLoadingSalvar = false;

        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }

        this.alertService.show('Sucesso', 'Configurações NFC-e salvas com sucesso!', AlertType.success);
        this.router.navigate(['/configuracoes/notas-fiscais']);
      },
      error: () => {
        this.isLoadingSalvar = false;
        this.alertService.show('Erro', 'Falha ao sincronizar exceções.', AlertType.error);
      },
    });
  }

  cancelar(): void {
    this.router.navigate(['/configuracoes/notas-fiscais']);
  }
}
